import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Pause, Play, Clock } from 'lucide-react';
import SwipeCard from './SwipeCard';
import { useStudyTimer } from '@/hooks/useStudyTimer';
import { useAudio } from '@/hooks/useAudio';
import { addPerfectToday } from '@/lib/perfect-today';
import { createSessionInit } from '@/lib/session-init';
import type { Word, SwipeResult, TaskType, InterruptedSession } from '@/types';

const SESSION_STORAGE_KEY = 'locomoco_interrupted_session';
const DEFAULT_DURATION = 30 * 60; // 30分

interface StudySessionProps {
  taskId: string;
  taskType: TaskType;
  words: Word[];
  dayStartHour?: number;
  initialState?: InterruptedSession;
  /** もう一周: 今日の完璧キャッシュを無視して全単語から再開 */
  ignorePerfectToday?: boolean;
  /** false = 任意復習など、ストリーク・タスク完了を記録しない */
  enableDailyGoal?: boolean;
  /** タイマー0 or 全単語クリア時（1日1回） */
  onDailyGoalMet: (results: Record<string, SwipeResult>) => void | Promise<void>;
  /** セッション終了時（ホームに戻る） */
  onSessionEnd: (results: Record<string, SwipeResult>) => void | Promise<void>;
  onInterrupt: () => void;
}

// 配列をシャッフル
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function StudySession({
  taskId,
  taskType,
  words,
  dayStartHour = 0,
  initialState,
  ignorePerfectToday = false,
  enableDailyGoal = true,
  onDailyGoalMet,
  onSessionEnd,
  onInterrupt,
}: StudySessionProps) {
  const { speak, stop, isPlaying } = useAudio();

  const sessionInit = createSessionInit(
    words,
    initialState,
    ignorePerfectToday,
    dayStartHour,
  );

  /** もう一周 / 全単語完璧後の再開: 今日の完璧キャッシュを使わない */
  const [freeReplay, setFreeReplay] = useState(sessionInit.freeReplay);
  const skipPerfectCache = ignorePerfectToday || freeReplay;

  const [perfectWordIds, setPerfectWordIds] = useState(sessionInit.perfectWordIds);
  const [results, setResults] = useState<Record<string, SwipeResult>>(
    initialState?.results ?? {}
  );

  const buildDeck = useCallback(
    (excludeIds: Set<string>) =>
      shuffle(words.filter((w) => !excludeIds.has(w.id))),
    [words]
  );

  const [deck, setDeck] = useState<Word[]>(sessionInit.deck);
  const [deckIndex, setDeckIndex] = useState(sessionInit.deckIndex);
  const [roundCount, setRoundCount] = useState(sessionInit.roundCount);
  const [cardKey, setCardKey] = useState(0);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showAddTime, setShowAddTime] = useState(false);
  const [dailyGoalMet, setDailyGoalMet] = useState(initialState?.dailyGoalMet ?? false);

  const dailyGoalMetRef = useRef(initialState?.dailyGoalMet ?? false);
  const resultsRef      = useRef<Record<string, SwipeResult>>(initialState?.results ?? {});

  // リワインド用の履歴スタック
  type HistoryEntry = { word: Word; deck: Word[]; deckIndex: number; roundCount: number };
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  const initialSeconds = initialState?.remainingSeconds ?? DEFAULT_DURATION;
  const targetSeconds = initialState?.targetSeconds ?? DEFAULT_DURATION;
  const [totalTarget, setTotalTarget] = useState(targetSeconds);

  useEffect(() => { resultsRef.current = results; }, [results]);

  const handleReplayFromCompletion = useCallback(() => {
    setFreeReplay(true);
    setPerfectWordIds(new Set());
    setDeck(shuffle([...words]));
    setDeckIndex(0);
    setRoundCount(1);
    setShowCompletion(false);
    setShowAddTime(false);
    setCardKey((k) => k + 1);
  }, [words]);

  const triggerDailyGoalMet = useCallback(async () => {
    if (!enableDailyGoal || dailyGoalMetRef.current) return;
    dailyGoalMetRef.current = true;
    setDailyGoalMet(true);
    await onDailyGoalMet(resultsRef.current);
  }, [enableDailyGoal, onDailyGoalMet]);

  const handleTimerComplete = useCallback(() => {
    void triggerDailyGoalMet();
    setShowAddTime(true);
  }, [triggerDailyGoalMet]);

  const { remaining, isPaused, pause, resume, addTime, formatTime } =
    useStudyTimer(initialSeconds, handleTimerComplete);

  // 中断復帰時: タイマーが既に0なら完了扱い＋継続モーダル
  useEffect(() => {
    if (enableDailyGoal && initialSeconds <= 0 && !dailyGoalMetRef.current) {
      void triggerDailyGoalMet();
      setShowAddTime(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // deckIndex が deck 範囲外にならないよう安全にクランプ
  const safeIndex   = deck.length > 0 ? Math.min(deckIndex, deck.length - 1) : 0;
  const currentWord = deck.length > 0 ? deck[safeIndex] ?? null : null;

  // セッション状態をlocalStorageに保存
  const saveSession = useCallback(() => {
    if (typeof window === 'undefined') return;
    const state: InterruptedSession = {
      taskId,
      taskType,
      words,
      perfectWordIds: Array.from(perfectWordIds),
      deckWordIds: deck.map((w) => w.id),
      currentDeckIndex: deckIndex,
      roundCount,
      results,
      remainingSeconds: remaining,
      targetSeconds: totalTarget,
      dailyGoalMet: dailyGoalMetRef.current,
      ignorePerfectToday: skipPerfectCache,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
  }, [taskId, taskType, words, perfectWordIds, deck, deckIndex, roundCount, results, remaining, totalTarget, skipPerfectCache]);

  // 中断してホームに戻る
  const handleInterrupt = useCallback(() => {
    stop();
    pause();
    saveSession();
    onInterrupt();
  }, [stop, pause, saveSession, onInterrupt]);

  // リワインド処理（1つ前のカードに戻る）
  // 注: state updater 内で他の setState を呼ぶと React concurrent mode で問題が出るため
  //     先に history を読んでから flushSync 相当に分けて更新する
  const handleRewind = useCallback(() => {
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      // 次の tick で他の state を更新（updater 内副作用を回避）
      setTimeout(() => {
        stop();
        setDeck(last.deck);
        setDeckIndex(last.deckIndex);
        setRoundCount(last.roundCount);
        setCardKey((k) => k + 1);
      }, 0);
      return prev.slice(0, -1);
    });
  }, [stop]);

  // スワイプ処理
  const handleSwipe = useCallback(
    (result: SwipeResult) => {
      if (!currentWord) return;

      // リワインド用に現在状態を履歴に積む（最大20件）
      setHistory((prev) => [
        ...prev.slice(-19),
        { word: currentWord, deck: [...deck], deckIndex, roundCount },
      ]);

      // 結果を記録
      setResults((prev) => ({ ...prev, [currentWord.id]: result }));

      // 完璧判定 → 今日のデッキから除外
      const newPerfect =
        result === 'perfect'
          ? new Set([...perfectWordIds, currentWord.id])
          : perfectWordIds;

      if (result === 'perfect') {
        if (!skipPerfectCache) addPerfectToday(currentWord.id, dayStartHour);
        setPerfectWordIds(newPerfect);
      }

      const nextIndex = deckIndex + 1;

      if (nextIndex >= deck.length) {
        // デッキを使い切ったら周回数を増やして再シャッフル（完璧以外）
        const remainingWords = words.filter((w) => !newPerfect.has(w.id));

        if (remainingWords.length === 0) {
          void triggerDailyGoalMet();
          setShowCompletion(true);
          return;
        }

        const newDeck = shuffle(remainingWords);
        setRoundCount((r) => r + 1);
        setDeck(newDeck);
        setDeckIndex(0);        // 必ず 0 から始める（境界外防止）
        setCardKey((k) => k + 1);
      } else {
        // 念のため上限チェック
        const clampedNext = Math.min(nextIndex, deck.length - 1);
        setDeckIndex(clampedNext);
        setCardKey((k) => k + 1);
      }
    },
    [currentWord, deck, deckIndex, roundCount, perfectWordIds, words, triggerDailyGoalMet, skipPerfectCache, dayStartHour]
  );

  // 追加時間を選択してセッション継続
  const handleAddTime = (seconds: number) => {
    setTotalTarget((t) => t + seconds);
    addTime(seconds);
    setShowAddTime(false);
  };

  // セッション終了（ホームへ）
  const handleFinishSession = useCallback(() => {
    stop();
    if (typeof window !== 'undefined') {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
    void onSessionEnd(resultsRef.current);
  }, [stop, onSessionEnd]);

  const taskLabel =
    taskType === 'LEARNING'
      ? 'Daily Spiral'
      : taskType === 'REVIEW_G'
      ? 'Spiral Review'
      : 'Weakness Focus';

  const progressPercent = totalTarget > 0 ? (remaining / totalTarget) * 100 : 0;

  const perfectCount = perfectWordIds.size;
  const totalWords   = words.length;
  const cardPosition = deck.length > 0 ? safeIndex + 1 : 0;
  const deckSize     = deck.length;

  if (showCompletion) {
    return (
      <div className="fixed inset-0 bg-slate-50 dark:bg-[#020617] z-50 flex flex-col items-center justify-center p-8">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="text-6xl mb-6">🎉</div>
          <h2 className="text-3xl font-black mb-2">全単語クリア！</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-2">
            {totalWords}単語すべてを「もう完璧」にしました
          </p>
          {dailyGoalMet && (
            <p className="text-emerald-500 font-bold mb-8">✓ 今日のタスク完了</p>
          )}
          {!dailyGoalMet && <div className="mb-4" />}
          <button
            onClick={handleReplayFromCompletion}
            className="w-full py-4 bg-violet-600 text-white font-bold rounded-2xl text-lg mb-3 active:scale-[0.98] transition-transform"
          >
            もう一周して続ける
          </button>
          <button
            onClick={handleFinishSession}
            className="w-full py-4 bg-blue-600 text-white font-bold rounded-2xl text-lg"
          >
            ホームに戻る
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-50 dark:bg-[#020617] z-50 flex flex-col no-select" style={{ height: '100dvh' }}>
      {/* ヘッダー */}
      <header className="pt-safe px-4 pb-2 flex items-center gap-2 shrink-0">
        <button
          onClick={handleInterrupt}
          className="p-3 -ml-1 rounded-full active:bg-slate-200 dark:active:bg-slate-800 transition-colors shrink-0"
        >
          <ArrowLeft size={22} className="text-slate-600 dark:text-slate-400" />
        </button>

        {/* 中央: 周回数 + 進捗 */}
        <div className="flex-1 flex flex-col items-center min-w-0">
          {skipPerfectCache && (
            <span className="text-[10px] font-black text-violet-400 bg-violet-500/10 px-2 py-0.5 rounded-full mb-0.5">
              もう一周
            </span>
          )}
          {dailyGoalMet && (
            <span className="text-[10px] font-black text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full mb-0.5">
              ✓ 今日のタスク完了
            </span>
          )}
          <div className="flex items-center gap-2">
            <span className="text-base font-black text-slate-700 dark:text-slate-200 tabular-nums [font-family:var(--font-game)]">
              {roundCount}周目
            </span>
            <span className="text-xs text-slate-400 tabular-nums [font-family:var(--font-game)]">
              {cardPosition} / {deckSize}
            </span>
          </div>
          {perfectCount > 0 && (
            <span className="text-[10px] text-yellow-500 font-bold tabular-nums">
              ⭐ {perfectCount} 完璧
            </span>
          )}
        </div>

        {/* タイマー */}
        <button
          onClick={isPaused ? resume : pause}
          className="flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-slate-100 dark:bg-slate-800 active:scale-95 transition-transform shrink-0"
        >
          {isPaused ? (
            <Play size={13} className="text-blue-500" />
          ) : (
            <Pause size={13} className="text-slate-500 dark:text-slate-400" />
          )}
          <span
            className={`text-sm font-black tabular-nums [font-family:var(--font-game)] ${
              remaining < 60
                ? 'text-red-500 animate-pulse'
                : remaining < 180
                ? 'text-orange-500'
                : 'text-slate-700 dark:text-slate-300'
            }`}
          >
            {formatTime(remaining)}
          </span>
        </button>
      </header>

      {/* タイマープログレスバー */}
      <div className="h-1 w-full bg-slate-200 dark:bg-slate-800 shrink-0">
        <motion.div
          className={`h-full ${
            remaining < 60 ? 'bg-red-500' : remaining < 180 ? 'bg-orange-400' : 'bg-blue-500'
          }`}
          animate={{ width: `${progressPercent}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* ポーズオーバーレイ */}
      <AnimatePresence>
        {isPaused && !showAddTime && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-40 bg-black/70 flex flex-col items-center justify-center gap-5 p-6"
          >
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center">
              <Clock size={40} className="text-white" />
            </div>
            <p className="text-white text-2xl font-black">一時停止中</p>
            <button
              onClick={resume}
              className="w-full max-w-xs flex items-center justify-center gap-2 py-4 bg-blue-600 text-white font-black rounded-2xl text-lg active:scale-95 transition-transform shadow-xl shadow-blue-600/30"
            >
              <Play size={22} fill="white" />
              再開する
            </button>
            <button
              onClick={handleInterrupt}
              className="text-slate-300 py-3 px-6 rounded-xl active:bg-white/10 text-sm font-medium"
            >
              ホームに戻る（進捗を保存）
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 追加時間選択モーダル */}
      <AnimatePresence>
        {showAddTime && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-40 bg-black/70 flex flex-col items-end justify-end p-0"
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="w-full bg-white dark:bg-slate-900 rounded-t-3xl p-6 pb-safe shadow-2xl"
            >
              <div className="w-10 h-1 bg-slate-200 dark:bg-slate-700 rounded-full mx-auto mb-5" />
              <div className="text-center mb-5">
                <div className="text-4xl mb-2">{dailyGoalMet ? '🎉' : '⏱'}</div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white">
                  {dailyGoalMet
                    ? `${Math.floor(totalTarget / 60)}分達成！今日のタスク完了`
                    : `${Math.floor(totalTarget / 60)}分が経過しました！`}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                  {dailyGoalMet
                    ? 'ストリーク更新済み。続けて学習できます'
                    : '続けて学習しますか？'}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2.5 mb-3">
                {[5, 10, 15, 20, 25, 30].map((min) => (
                  <button
                    key={min}
                    onClick={() => handleAddTime(min * 60)}
                    className="py-4 rounded-2xl font-black text-base bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 active:scale-95 active:bg-blue-100 transition-all"
                  >
                    +{min}分
                  </button>
                ))}
              </div>
              <button
                onClick={handleFinishSession}
                className="w-full py-4 rounded-2xl font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 active:scale-98 active:bg-slate-200 transition-all"
              >
                今日はここまで
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* カードエリア: flex-1 で残り高さをすべて取り、SwipeCard に渡す */}
      <main className="flex-1 flex flex-col px-3 pt-3 pb-safe overflow-hidden min-h-0">
        {!currentWord && !showAddTime && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6 text-center">
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              今日の完璧済み単語が多いため、デッキを再構築します
            </p>
            <button
              onClick={handleReplayFromCompletion}
              className="px-6 py-3 bg-violet-600 text-white font-bold rounded-2xl active:scale-95 transition-transform"
            >
              もう一周して再開
            </button>
          </div>
        )}
        <AnimatePresence mode="wait">
          {currentWord && (
            <motion.div
              key={`${currentWord.id}-${cardKey}`}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="flex-1 flex flex-col min-h-0"
            >
              <SwipeCard
                word={currentWord}
                onSwipe={handleSwipe}
                onRewind={handleRewind}
                canRewind={history.length > 0}
                speak={speak}
                stop={stop}
                isAudioPlaying={isPlaying}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
